/**
 * Both guarded so a plain Node test can import anything that reaches config:
 * `window` and `import.meta.env` are Vite/browser facts, not language ones.
 */
const SEARCH = globalThis.location?.search ?? "";
const ENV: Record<string, string | undefined> =
  (import.meta as unknown as { env?: Record<string, string | undefined> }).env ?? {};

/** Hostnames that mean "this device", whichever device is asking. */
const LOOPBACK = new Set(["localhost", "127.0.0.1", "::1", "[::1]"]);

/**
 * Which venue server this page talks to.
 *
 * `?server=ws://host:port` wins, so QA can point a built bundle at another
 * server without a rebuild. Otherwise the configured address is used — except
 * for one case that is otherwise a dead end.
 *
 * Testing on a phone means opening the dev server by its LAN address. The dev
 * env says `ws://localhost:2567`, and localhost on a phone is the PHONE, which
 * is running nothing. The page loads, the venue never answers, and the only
 * thing the fan sees is "could not reach the venue" — with a working server
 * sitting on the laptop the page just came from.
 *
 * So when the configured server is loopback but the page is NOT, the page's own
 * hostname is used instead: whatever host served this file is the host running
 * the server. Production is untouched, because its address is a real domain and
 * never matches the loopback test.
 */
function resolveServerUrl(): string {
  const override = new URLSearchParams(SEARCH).get("server");
  if (override) return override;

  const configured = ENV.VITE_SERVER_URL ?? "ws://localhost:2567";
  const pageHost = globalThis.location?.hostname ?? "";
  if (!pageHost || LOOPBACK.has(pageHost)) return configured;

  try {
    const url = new URL(configured);
    if (!LOOPBACK.has(url.hostname)) return configured;
    url.hostname = pageHost;
    // Rebuilt by parts rather than toString(), which appends a trailing slash.
    return `${url.protocol}//${url.host}`;
  } catch {
    return configured;
  }
}

export const clientConfig = {
  serverUrl: resolveServerUrl(),
  roomName: "concert",

  /**
   * 1-on-1 voice. Off unless `VITE_VOICE_ENABLED=1`.
   *
   * Off by default for three reasons that all point the same way. It is first
   * on the documented cut list (spec 8). There is no TURN relay, and without
   * one a call fails outright behind symmetric NAT, which is a real share of
   * Indian mobile carriers — so for those fans the feature is a button that
   * does nothing and explains nothing. And the build spec lists voice chat
   * among the things this venue is not.
   *
   * The code is kept rather than deleted: it works on a good network, the
   * consent flow and the report path are built and tested, and turning it on
   * is one variable once a TURN server exists. Deleting it would make that a
   * rewrite instead.
   */
  voiceEnabled: (ENV.VITE_VOICE_ENABLED ?? "").trim() === "1",
};
